import {useAtomValue} from 'jotai';
import {isEqual} from 'lodash';
import {memo, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {getCategoryIcon} from '@/components/CategoryIcon';
import {Typography, typographyStyle} from '@/components/Typography';
import {Franchisee} from '@/map/@types';
import {starredBrandsInfoAtom} from '@/map/atom';
import {MAP_LAYER_Z_INDEX} from '@/map/constants';
import {useOverlayScroll} from '@/map/hooks';
import {Marker} from '@/map/modules';
import {Flexbox} from '@/styled-components';
import {palette} from '@/styles';
import {getCategoryId, getCategoryInfoFromAlias} from '@/utils';
import {RoundColorContent} from '../RoundColorMarker/RoundColorContent';
import {Arrow, Container, CountBadge, DetailBox, Marker as MarkerBase} from '../StyledComponents';
import StarBadge from './star-badge.svg';

interface IProps extends naver.maps.MarkerOptions {
  item: Franchisee & {
    overlapped?: Franchisee[];
  };
}

const MAX_OVERLAPPED_MARKER_COUNT = 3;

const containerAnchor = {position: 'bottom', x: -12, y: 6, alignLeft: true};

const starredHoverStyles = {
  borderColor: palette.primary400,
};

const defaultHoverStyles = {
  borderColor: palette.gray800,
};

export const BrandMarker = memo(
  ({item, ...props}: IProps) => {
    const starredBrand = useAtomValue(starredBrandsInfoAtom);
    const isStarredBrand = (item: Franchisee) => {
      if (!item?.brandId) {
        return false;
      }

      return starredBrand.selected.findIndex((i) => i === item.brandId) >= 0;
    };

    const sorted = useMemo(() => {
      return (item.overlapped ?? []).concat(item);
    }, [item]);

    const [hover, setHover] = useState<boolean>(false);

    const detailScrollBoxRef = useRef<HTMLDivElement | null>(null);

    useOverlayScroll(sorted?.length > 1 && hover);

    if (sorted?.length > 1) {
      // 중첩마커
      const hasStarred = sorted.some((i) => isStarredBrand(i));

      return (
        <Marker draggable={false} {...props}>
          <Container
            options={{
              zIndex: MAP_LAYER_Z_INDEX.CATEGORY_BRAND,
              hover: hasStarred ? starredHoverStyles : defaultHoverStyles,
            }}
            anchor={containerAnchor}
            onMouseOver={() => setHover(true)}
            onMouseOut={() => setHover(false)}
          >
            <Content>
              {sorted.length > MAX_OVERLAPPED_MARKER_COUNT && (
                <CountBadge>
                  +{sorted.length - MAX_OVERLAPPED_MARKER_COUNT}
                </CountBadge>
              )}
              {hasStarred && <StarIconBadge />}
              <IconGroup>
                {sorted
                  .slice(0, MAX_OVERLAPPED_MARKER_COUNT)
                  .map((i) =>
                    isStarredBrand(i) ? (
                      <BrandLogo key={i.id} item={i} border />
                    ) : (
                      <CategoryIcon key={i.id} item={i} border />
                    )
                  )}
              </IconGroup>
              <SubMenuWrapper className={hover ? 'show' : ''}>
                <DetailBox ref={detailScrollBoxRef}>
                  {sorted.map((i) => (
                    <OverlappedFranchisee key={i.id}>
                      <Flexbox alignItems="flex-start">
                        <LogoWrapper>
                          {isStarredBrand(i) ? (
                            <>
                              <BrandLogo item={i} />
                              <StarIconBadge width={8} height={8} />
                            </>
                          ) : (
                            <CategoryIcon item={i} />
                          )}
                        </LogoWrapper>
                        <LineEllipsis>
                          {i.brandName} {i.name}
                        </LineEllipsis>
                      </Flexbox>
                    </OverlappedFranchisee>
                  ))}
                </DetailBox>
              </SubMenuWrapper>
            </Content>
            <Arrow position="top-left" />
          </Container>
        </Marker>
      );
    } else {
      // 단일마커
      const item = sorted[0];
      const brandName = item.brandName;
      const starred = isStarredBrand(item);

      return (
        <Marker draggable={false} {...props}>
          <Container
            options={{
              zIndex: MAP_LAYER_Z_INDEX.CATEGORY_BRAND,
              hover: starred ? starredHoverStyles : defaultHoverStyles,
            }}
            anchor={containerAnchor}
            onMouseOver={() => setHover(true)}
            onMouseOut={() => setHover(false)}
          >
            <Content>
              {starred && <StarIconBadge />}
              {starred ? (
                <BrandLogo item={item} />
              ) : (
                <CategoryIcon item={item} />
              )}
              <Typography
                variant="medium14"
                as="div"
                fontWeight="700"
                ellipsis
                title={brandName}
              >
                {brandName.substring(0, 8)}
                {brandName.length > 8 && <span>...</span>}
              </Typography>
              <SubMenuWrapper className={hover ? 'show' : ''}>
                <SubText>{item.name}</SubText>
              </SubMenuWrapper>
            </Content>
            <Arrow position="top-left" />
          </Container>
        </Marker>
      );
    }
  },
  (prev, next) => isEqual(prev, next)
);

const CategoryIcon = ({
  item,
  border,
}: {
  item: IProps['item'];
  border?: boolean;
}) => {
  const alias = item?.businessItemAlias;
  const {category} = getCategoryInfoFromAlias(alias || []);
  const categoryId = getCategoryId(category || '');
  const icon = getCategoryIcon(categoryId, 16);

  return <Logo size="small" border={border} icon={icon} />;
};

const BrandLogo = ({
  item,
  border,
}: {
  item: IProps['item'];
  border?: boolean;
}) => {
  const logoUrl = item?.logoUrl;

  if (!logoUrl) {
    return <CategoryIcon item={item} border={border} />;
  }

  return (
    <Logo
      size="small"
      color={logoUrl ? palette.white : palette.gray200}
      border={border}
    >
      <img src={logoUrl} alt="로고" />
    </Logo>
  );
};

const StarIconBadge = styled('img').attrs({
  src: StarBadge,
  alt: '관심',
})`
  position: absolute;
`;

const Logo = styled(RoundColorContent)<{border?: boolean}>`
  flex-shrink: 0;
  ${({border}) => border && `border: solid 1px ${palette.gray200};`}
`;

const LogoWrapper = styled.div`
  position: relative;
  ${StarIconBadge} {
    right: -4px;
    top: 0;
  }
`;

const LineEllipsis = styled(Typography)`
  overflow: hidden;
  width: 116px;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const Content = styled(MarkerBase)`
  position: relative;
  display: flex;
  align-items: center;
  ${CountBadge} {
    position: absolute;
    right: -8px;
    top: -4px;
  }
  ${Typography} {
    margin-left: 4px;
  }
  & > ${StarIconBadge} {
    left: -4px;
    top: -2px;
  }
`;

const SubText = styled('div')`
  ${{...typographyStyle.medium10}}
  color: ${palette.textSecondary};
  font-weight: 500;
  padding: 2px 4px;
  border-radius: 3px;
  border: solid 1px ${palette.gray300};
  white-space: nowrap;
  background-color: ${palette.white};
`;

const IconGroup = styled.div`
  display: flex;
  ${Logo}:not(:first-of-type) {
    margin-left: -4px;
  }
`;

const OverlappedFranchisee = styled('div')`
  color: ${palette.gray200};
  & + & {
    margin-top: 8px;
  }
  & > ${Typography} {
    display: none;
    margin-top: 2px;
  }
  &:hover {
    color: ${palette.white};
    & ${LineEllipsis} {
      display: block;
    }
  }
`;

const SubMenuWrapper = styled.div`
  position: absolute;
  left: 50%;
  top: 100%;
  transform: translateX(-50%);
  padding-top: 4px;
  display: none;
  &.show {
    display: block;
  }
  ${DetailBox} {
    max-width: 160px;
    max-height: 140px;
    overflow: auto;
  }
`;
